import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useQuiz } from '../../hooks/useQuiz';
import { trackPixelEvent } from '../MetaPixel';
import { OptionCard } from './OptionCard';
import { LeadForm } from './LeadForm';
import { QuizResult } from './QuizResult';
import { Reveal } from '../ui/Reveal';
import type { LeadDetails } from '../../types/quiz';
const EASE = [0.23, 1, 0.32, 1] as const;
export function QuizSection() {
  const quiz = useQuiz();
  const {
    stage,
    question,
    index,
    total
  } = quiz;
  const progress = stage === 'intro' ? 0 : stage === 'questions' ? (index + 1) / (total + 1) * 100 : 100;
  const handleStart = () => {
    trackPixelEvent('StartTrial', {
      content_name: 'Test — inicio'
    });
    quiz.start();
  };
  const handleLead = (details: LeadDetails) => {
    trackPixelEvent('Lead', {
      content_name: 'Test completado',
      code: quiz.result.name
    });
    quiz.submitLead(details);
  };
  return <section id="test" className="scroll-mt-4 bg-ivory px-6 py-20 sm:py-28" data-id="element-264" data-mp="2q7k82t1ms34e">
      <div className="mx-auto max-w-2xl" data-id="element-265" data-mp="2u72jmy7kb1uk">
        <Reveal data-id="element-266" data-mp="2u8439r2n8wnw">
          <div className="text-center" data-id="element-267" data-mp="2u9nlhn7v1hsb">
            <p className="text-[10px] font-semibold uppercase tracking-[0.26em] text-gold-dark sm:text-[11px]" data-id="element-268" data-mp="2z6nqdusz4ehu">
              Test de autoobservación
            </p>
            <h2 className="mt-5 font-serif text-[1.75rem] font-light leading-tight text-ink sm:text-4xl" data-id="element-269" data-mp="39538x9dxqffq">
              Descubre qué está bloqueando{' '}
              <em className="not-italic text-gold-dark" data-id="element-270" data-mp="3d55cjo38dixn">tu siguiente nivel</em>
            </h2>
            <p className="mx-auto mt-5 max-w-lg text-[17px] leading-relaxed text-ink-soft sm:text-[18.5px]" data-id="element-271" data-mp="3hyq5h1pyvmpz">
              Responde estas preguntas y descubre cuál de los tres códigos
              necesita hoy mayor atención en tu vida.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1} data-id="element-272" data-mp="qct1u6qakrs">
          <div className="relative mt-10 overflow-hidden rounded-[1.75rem] border border-gold/30 bg-cream shadow-soft" data-id="element-273" data-mp="pjjklgdxh5y">
            {/* Progress bar */}
            {stage !== 'intro' && <div className="h-1 w-full bg-nude/70" data-id="element-274" data-mp="3290cdtvwurfd">
                <motion.div className="h-full bg-gold-dark" initial={false} animate={{
              width: `${progress}%`
            }} transition={{
              duration: 0.4,
              ease: EASE
            }} data-id="element-275" data-mp="3gwv9rnkv5jys" />
              </div>}

            <div className="px-6 py-9 sm:px-10 sm:py-11" data-id="element-276" data-mp="3kxymi17m6zvs">
              <AnimatePresence mode="wait" initial={false} data-id="element-277" data-mp="3kxf3eh68a5g6">
                {/* ---------- INTRO ---------- */}
                {stage === 'intro' && <motion.div key="intro" initial={{
                opacity: 0,
                y: 14
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.28,
                ease: EASE
              }} className="text-center" data-id="element-278" data-mp="3uvl5ozvy5dlk">
                    <p className="mx-auto max-w-md text-[16px] leading-relaxed text-ink-soft" data-id="element-279" data-mp="g0krunc9eabf">
                      No existen respuestas correctas o incorrectas. Responde
                      desde la honestidad. Este test es un espacio de
                      autoobservación, no un diagnóstico clínico.
                    </p>
                    <p className="mt-5 text-[11px] uppercase tracking-[0.18em] text-ink-muted" data-id="element-280" data-mp="b6zv0ymejmdt">
                      10 preguntas · 3 minutos
                    </p>
                    <button type="button" onClick={handleStart} className="btn-gold mt-8 w-full rounded-full px-8 py-4 text-[11px] font-semibold uppercase tracking-[0.18em] sm:w-auto sm:text-xs" data-id="element-281" data-mp="1cpyd4qfg1xa">
                      Comenzar el test
                    </button>
                  </motion.div>}

                {/* ---------- QUESTIONS ---------- */}
                {stage === 'questions' && question && <motion.div key={`q-${question.id}`} initial={{
                opacity: 0,
                x: 24
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -24
              }} transition={{
                duration: 0.28,
                ease: EASE
              }} data-id="element-282" data-mp="3ipjpnzsbdqre">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-gold-dark" data-id="element-283" data-mp="34u7i2rt285t2">
                      Pregunta {index + 1} de {total}
                    </p>

                    <h3 className="mt-4 font-serif text-[1.6rem] font-light leading-snug text-ink sm:text-[2rem]" data-id="element-284" data-mp="2uzytgf75gx06">
                      {question.prompt}
                    </h3>

                    {question.helper && <p className="mt-3 text-[15px] leading-relaxed text-ink-muted" data-id="element-285" data-mp="2h2aibz2vx48b">
                        {question.helper}
                      </p>}

                    {question.kind === 'open' ? <textarea value={quiz.openText} onChange={e => quiz.setOpenText(e.target.value)} rows={5} placeholder="Escribe aquí…" aria-label={question.prompt} className="mt-6 w-full resize-none rounded-2xl border border-gold/30 bg-ivory px-5 py-4 text-[15px] leading-relaxed text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-gold/40" data-id="element-286" data-mp="rly5krmc7wgw" /> : <div className={`mt-6 grid gap-3 ${question.kind === 'scale' ? '' : 'sm:grid-cols-1'}`} role="group" aria-label={question.prompt} data-id="element-287" data-mp="hvddzo52zs7h">
                        {question.options?.map(option => <OptionCard key={option.label} label={option.label} text={option.text} variant={question.kind === 'scale' ? 'scale' : 'choice'} selected={quiz.currentAnswer === option.label} onSelect={() => quiz.select(question.id, option.label)} data-id="element-288" data-mp="3q9c6gfsugvg8" />)}
                      </div>}

                    <div className="mt-8 flex items-center justify-between gap-4" data-id="element-289" data-mp="ejngt2txqslh">
                      <button type="button" onClick={quiz.back} disabled={index === 0} className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-ink-muted transition-colors duration-200 hover:text-ink disabled:pointer-events-none disabled:opacity-0" data-id="element-290" data-mp="9ku61i619fp0">
                        <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" data-id="element-291" data-mp="1uy58ax4i08ab" />
                        Atrás
                      </button>

                      <button type="button" onClick={quiz.next} disabled={!quiz.canContinue} className="btn-gold inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-[11px] font-semibold uppercase tracking-[0.18em] disabled:pointer-events-none disabled:opacity-40" data-id="element-292" data-mp="27f7ptoxvbb2a">
                        {quiz.isLast ? 'Finalizar' : 'Continuar'}
                        <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" data-id="element-293" data-mp="2q78y1hwj8dod" />
                      </button>
                    </div>
                  </motion.div>}

                {/* ---------- LEAD CAPTURE ---------- */}
                {stage === 'capture' && <motion.div key="capture" initial={{
                opacity: 0,
                x: 24
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -24
              }} transition={{
                duration: 0.28,
                ease: EASE
              }} data-id="element-294" data-mp="343mdklhia507">
                    <LeadForm onSubmit={handleLead} data-id="element-295" data-mp="141ecehcgmi5y" />
                  </motion.div>}

                {/* ---------- RESULT ---------- */}
                {stage === 'result' && <motion.div key="result" initial={{
                opacity: 0,
                y: 18
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.3,
                ease: EASE
              }} data-id="element-296" data-mp="v3u9z52v2ftr">
                    <QuizResult result={quiz.result} nombre={quiz.lead?.nombre.split(' ')[0]} onRestart={quiz.restart} data-id="element-297" data-mp="1ir2ehksgtkuq" />
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>
        </Reveal>
      </div>
    </section>;
}